/**
 * "Learn Medsender" tutorial definitions.
 *
 * Tutorials are pure data so that adding a v1.1 tutorial (assign, send fax,
 * split/edit) is a config change only — no engine changes. A definition is:
 *
 *   {
 *     id:          stable string, also the localStorage progress key
 *     title:       menu label
 *     description: menu sub-label
 *     path:        route the tutorial runs on; the runner navigates there first
 *     requires:    optional (context) => bool — gates availability
 *     unavailableReason: tooltip shown in the menu when `requires` fails
 *     buildSteps:  (context) => step[]   (lets one tutorial have variants)
 *   }
 *
 * A step is:
 *   {
 *     selector:  string | string[]   (first match wins; array = fallbacks)
 *     title, body
 *     advanceOn: 'click' → the user performs the real click to continue
 *                'next'  → informational, advanced by the tooltip's Next button
 *     optional:  true → step is dropped when its target is missing instead of
 *                failing the tutorial
 *     actionResult: optional action name whose successful result is required
 *                   before a click-advanced step can complete
 *   }
 *
 * Selector policy: real click targets carry a stable `data-tutorial` attribute
 * added to the source components. Purely contextual highlights (the records
 * table) reuse antd's structural `.ant-table-wrapper` class rather than adding
 * an attribute to a shared table used by every list in the app.
 */

import { assignActionLabel } from '../../global/featureFlags';

/** Every `data-tutorial` value this feature relies on, in one place. */
export const TUTORIAL_TARGETS = {
  newMenu: 'new-menu',
  newMenuUpload: 'new-menu-upload',
  uploadRecord: 'upload-record',
  sendRecord: 'send-record',
  chartAction: 'chart-action',
  archiveAction: 'archive-action',
  labelAction: 'label-action',
  recordSearch: 'record-search',
  recordFilters: 'record-filters',
  moreActions: 'more-actions',
  assignAction: 'assign-action',
  sendRecipient: 'send-recipient',
  sendSubmit: 'send-submit',
  splitRecord: 'split-record',
  splitPages: 'split-pages',
  chartSendToChart: 'chart-send-to-chart',
  chartCreateChart: 'chart-create-chart',
};

const target = (name) => `[data-tutorial="${name}"]`;

/**
 * The first row's selection checkbox on any records list (uploads + both
 * inboxes). Preferred over the whole `.ant-table-wrapper` because spotlighting
 * a full-width, full-height table dims nothing useful and pushes the tooltip
 * against the viewport edge; the checkbox is also exactly what the user has to
 * click. Falls back to the table if the row markup ever changes.
 */
const RECORD_CHECKBOX_SELECTOR = [
  '.ant-table-tbody tr.ant-table-row:first-child .ant-checkbox-wrapper',
  '.ant-table-tbody tr.ant-table-row:first-child .ant-checkbox',
  '.ant-table-wrapper',
];

/**
 * A records row that Assign Document will actually accept.
 *
 * Assign is disabled for a reply and for a Jotform submission (see
 * ReferralActionBar's `assignDoc` item and ReceivedRecords' "Assign Document"
 * entry — both gate on `isAReplySelected` and
 * `receiver_type === 'jotform_submission'`). `components/inbox` marks those rows
 * `record-row-reply` (assignment denied) / `record-row-form-submission`, so ineligible rows can be
 * filtered in the selector rather than discovered by selecting one.
 */
const ASSIGNABLE_ROW = 'tr.ant-table-row:not(.record-row-reply):not(.record-row-form-submission)';

/**
 * antd flags the rows currently ticked; used to check what the user selected.
 * Body-scoped so a table rendered in a sidebar cannot contribute to the count.
 */
const SELECTED_ROW = '.ant-table-tbody tr.ant-table-row.ant-table-row-selected';

/** antd renders one modal body per open modal; used for post-click steps. */
const MODAL_SELECTOR = '.ant-modal-content';

/** Only ever match the dropdown that is actually open. */
const OPEN_DROPDOWN = '.ant-dropdown:not(.ant-dropdown-hidden)';

export const UPLOADS_PATH = '/app/uploads';
export const INBOX_PATH = '/app/received';

/**
 * Selecting a record is deliberately advanced with the tooltip's Next button
 * rather than by the checkbox click itself.
 *
 * Ticking a checkbox makes antd re-render (and replace) the row, which destroys
 * the element driver.js is currently highlighting. driver.js then fails to
 * establish the *following* highlight at all: no active element means no
 * spotlight cut-out, and its `.driver-active * { pointer-events: none }` rule
 * leaves the next target unclickable, so the step becomes impossible to finish.
 * Verified in a real browser: with `advanceOn: 'click'` the label step reported
 * no active element and the button could not be clicked; with 'next' it
 * highlights and completes correctly. Neither moveTo/refresh nor rebuilding the
 * tour recovered it, so the sequence avoids the situation instead.
 */
const selectRecordStep = (verb) => ({
  selector: RECORD_CHECKBOX_SELECTOR,
  title: 'Pick a record',
  body: `Tick this checkbox to select the record, then continue. ${verb} actions only appear once something is selected.`,
  advanceOn: 'next',
  side: 'right',
  // Shown instead of the generic copy when this step's target never appears —
  // typically an empty or still-loading list after a filter change. The old
  // behaviour was to end the tour mutely after an 8s wait.
  unavailableHint: 'This step needs a record in the list. Clear your filters or search so at least one record is showing, then start the tutorial again.',
});

// A successful single upload ends in `window.open('/app/view/:id', '_self')`
// (UploadSingleRecord), a HARD navigation that destroys the JS context — so the
// tour cannot record its own completion from a timer or a teardown hook. The
// tutorial therefore declares `completesOn: 'upload'` and LearnMedsender writes
// progress synchronously inside the emitted event, before the navigation runs.
const uploadDocumentSteps = () => [
  {
    selector: [target(TUTORIAL_TARGETS.uploadRecord), target(TUTORIAL_TARGETS.newMenu)],
    title: 'Start an upload',
    body: 'Click Upload to bring a document into Medsender. You can also use the New button in the top bar from any screen.',
    advanceOn: 'click',
  },
  {
    selector: MODAL_SELECTOR,
    title: 'Choose your file',
    body: 'Drag a file in or browse for it, then add patient details. Medsender reads the document and pre-fills what it can.',
    advanceOn: 'next',
    optional: true,
  },
];

const sendToChartSteps = ({ hasEmrIntegration }) => {
  if (!hasEmrIntegration) {
    // Fax variant: an org with no EMR integration has no Chart Actions menu at
    // all, so teach the outbound path they actually have.
    return [
      selectRecordStep('Sending'),
      {
        selector: target(TUTORIAL_TARGETS.sendRecord),
        title: 'Send this document',
        body: 'Your organization has no EMR integration connected yet, so documents leave Medsender by fax or email. Click Send to choose a recipient.',
        advanceOn: 'click',
      },
      {
        selector: MODAL_SELECTOR,
        title: 'Pick a recipient',
        body: 'Search your contacts or type a fax number, then send. Once an EMR is connected, this same record can go straight to a patient chart.',
        advanceOn: 'next',
        optional: true,
      },
    ];
  }

  return [
    selectRecordStep('Chart'),
    {
      selector: target(TUTORIAL_TARGETS.chartAction),
      title: 'Open Chart Actions',
      body: 'Chart Actions holds everything that writes back to your EMR. Click it to open the menu.',
      advanceOn: 'click',
    },
    {
      // The menu ITEM, not the menu container. Highlighting the container meant
      // the click that actually files the document was never observed, so the
      // tour sat on "Step 3 of 3 … Done" describing an action the user had
      // already taken, floating over the Create New Chart modal it opened.
      //
      // Scoped to the dropdown that is actually open: several closed antd
      // dropdowns (the header "New" menu, the org switcher) also render
      // `.ant-dropdown-menu` into the DOM, and a bare selector highlighted
      // whichever came first — leaving the spotlight and the fake cursor
      // pointing at different elements.
      selector: [
        `${OPEN_DROPDOWN} ${target(TUTORIAL_TARGETS.chartSendToChart)}`,
        `${OPEN_DROPDOWN} ${target(TUTORIAL_TARGETS.chartCreateChart)}`,
        `${OPEN_DROPDOWN} .ant-dropdown-menu`,
      ],
      title: 'Send to Chart',
      body: 'Choose Send to Chart to file this document against a patient chart. Medsender matches the patient and uploads it for you.',
      // Last step: performing the click IS finishing the tutorial.
      advanceOn: 'click',
      optional: true,
    },
  ];
};

// IMPORTANT: the archive click must be the LAST step. Archiving from the records
// list ends in `window.location.reload()` (see UploadedRecords#archiveRecord),
// which destroys the JS context. Completion is only recorded synchronously when
// the clicked step is the final one — any step after it (even an optional one)
// makes the runner await a target that the reload never lets it see, so the
// tutorial would silently never be marked complete.
const archiveDocumentSteps = () => [
  selectRecordStep('Archive'),
  {
    selector: target(TUTORIAL_TARGETS.archiveAction),
    title: 'Archive it',
    body: 'Archiving clears a record out of your working list without deleting it — nothing is lost, and you can switch this list to “Archived records” to find it again. Click Archive to finish.',
    advanceOn: 'click',
    actionResult: 'archive',
  },
];

const filtersSearchLabelsSteps = () => [
  {
    // The redesigned inbox is the only inbox now (ENG-3557 removed the
    // `inbox_redesign` flag); the fallback selector stays as a defensive alias for
    // the redesigned search control.
    selector: [target(TUTORIAL_TARGETS.recordSearch), '.referral-filter-bar__search'],
    // `data-tutorial` lands on the inner <input> (antd Input.Search spreads
    // unknown props onto the control and puts className on its wrapper), so
    // without this the spotlight cut out the text field and left the search and
    // clear buttons outside it, clipping the control mid-widget.
    expandTo: '.ant-input-search',
    title: 'Search',
    body: 'Search matches patient names, senders and labels. Type here and press Enter to narrow the list.',
    advanceOn: 'next',
  },
  {
    selector: [target(TUTORIAL_TARGETS.recordFilters), '.referral-filter-bar__select'],
    title: 'Filter',
    body: 'Filters stack with search — combine status, date and department to build the view you want. Your choice stays in the URL, so you can bookmark it.',
    advanceOn: 'next',
  },
  selectRecordStep('Label'),
  {
    selector: target(TUTORIAL_TARGETS.labelAction),
    title: 'Add a label',
    body: 'Labels are your own tags for grouping records. Click here to add one — you can then filter or search by that label.',
    advanceOn: 'click',
  },
];

// The tour narrates the buttons by name, so every line quoting the assign action
// reads its label from the same helper the buttons themselves use — a tour that
// says "Assign Document" while the menu says "Add assignee" is a broken tour.
// `assignActionLabel()` is called here, inside the per-tour builder, so a flag
// that lands after this module is first imported still takes effect.
const assignRecordSteps = () => [
  {
    ...selectRecordStep('Assign'),
    // Point at a row Assign Document will actually accept.
    //
    // The shared select step spotlights `tr:first-child`, but the first row is
    // often a reply or a form submission — exactly the kinds Assign is disabled
    // for. The tour then walked the user into the overflow menu and asked them to
    // click a greyed-out "Assign Document" (reported from a real inbox). Dropping
    // `:first-child` and filtering the excluded kinds instead makes
    // `resolveStepElement` pick the first row that CAN be assigned; if the list
    // holds nothing but replies and form submissions, no target resolves and
    // LearnMedsender declines the tutorial with `unavailableReason` up front
    // rather than dead-ending mid-tour.
    selector: [
      `.ant-table-tbody ${ASSIGNABLE_ROW} .ant-checkbox-wrapper`,
      `.ant-table-tbody ${ASSIGNABLE_ROW} .ant-checkbox`,
    ],
    // Belt and braces for the user who ignores the spotlight (or starts the
    // tutorial with rows already selected from before): the step will not be left
    // until the selection is one that Assign Document accepts.
    gate: {
      check: (root) => {
        const selected = Array.from(root.querySelectorAll(SELECTED_ROW));
        if (selected.length !== 1) return false;
        return selected[0].matches(ASSIGNABLE_ROW);
      },
      message: `Select exactly one ordinary document to continue — replies and form submissions can’t be assigned, and ${assignActionLabel()} only accepts one record at a time.`,
    },
    body: 'Tick the checkbox on this record to select it. Replies and form submissions can’t be assigned, and Assign actions only appear once exactly one record is selected.',
    unavailableHint: 'This tutorial needs a record that can be assigned. Replies and form submissions are excluded, so clear your filters or search until an ordinary document is showing, then start the tutorial again.',
  },
  {
    // The redesigned inbox keeps Assign Document behind the "⋯" overflow menu;
    // the legacy inbox renders the Assign Document button directly in the
    // action bar (it carries the same data-tutorial attribute), so the fallback
    // makes this step work on both.
    selector: [target(TUTORIAL_TARGETS.moreActions), target(TUTORIAL_TARGETS.assignAction)],
    title: `Find ${assignActionLabel()}`,
    body: `${assignActionLabel()} lives here. Click to continue.`,
    advanceOn: 'click',
  },
  {
    // ONLY the Assign Document item itself, inside the dropdown that is actually
    // open. The old `.ant-dropdown:not(.ant-dropdown-hidden) .ant-dropdown-menu`
    // fallback was far too loose: once the overflow menu was closed it matched a
    // different open dropdown entirely and the spotlight jumped to the filter bar,
    // while the popover still said "Choose Assign Document". Same failure the
    // send-to-chart step already hit once — a menu-shaped selector is never
    // specific enough.
    //
    // `requireEnabled` because Assign Document is disabled for a reply, a Jotform
    // submission, or a multi-selection; without it the tour spotlights a greyed
    // item and asks for a click that can never land.
    selector: `${OPEN_DROPDOWN} ${target(TUTORIAL_TARGETS.assignAction)}`,
    requireEnabled: true,
    title: 'Assign the document',
    body: `Choose ${assignActionLabel()} to hand this record to a teammate.`,
    advanceOn: 'click',
    // Optional: on the legacy inbox the previous click was the Assign Document
    // button itself, so no dropdown opens and this step is skipped to the modal.
    optional: true,
    unavailableHint: 'This record can’t be assigned — replies and form submissions are excluded, and only one record at a time can be assigned. Pick a single ordinary document and start the tutorial again.',
  },
  {
    selector: MODAL_SELECTOR,
    title: 'Fill in the assignment',
    body: 'Pick a status and an assignee, and add a note if you like. Clicking Submit assigns the document — the tour ends here so you decide whether to send it.',
    advanceOn: 'next',
    optional: true,
  },
];

// The final steps deliberately advance with Next, never by clicking Send:
// click-advancing the Send button would transmit a real fax from a tutorial.
const sendFaxSteps = () => [
  selectRecordStep('Send'),
  {
    selector: target(TUTORIAL_TARGETS.sendRecord),
    title: 'Open the send dialog',
    body: 'Click Send to fax or email this record to someone outside Medsender.',
    advanceOn: 'click',
  },
  {
    selector: [target(TUTORIAL_TARGETS.sendRecipient), MODAL_SELECTOR],
    title: 'Add a recipient',
    body: 'Type a contact name, a fax number or an email address here. You can add several recipients to send one document to multiple places.',
    advanceOn: 'next',
    optional: true,
  },
  {
    selector: [target(TUTORIAL_TARGETS.sendSubmit), MODAL_SELECTOR],
    title: 'Ready to send',
    body: 'That’s the whole flow — the tour ends here. Click Send yourself when you’re ready to transmit for real, or Cancel to close without sending anything.',
    advanceOn: 'next',
    optional: true,
  },
];

// Nothing in this tutorial creates or destroys data: the tour ends on the
// split modal with instructions to Cancel, and never click-advances Split.
const splitDocumentSteps = () => [
  {
    // The document title is a react-router <Link> to /app/view/:id (see
    // containers/records/shared/constants.js#recordViewerPath), so clicking it
    // is SPA navigation — the tutorial survives it via `allowedPathPrefixes`.
    selector: [
      '.ant-table-tbody tr.ant-table-row:first-child a',
      '.ant-table-tbody tr.ant-table-row:first-child',
    ],
    title: 'Open a record',
    body: 'Click the document title to open it in the record viewer.',
    advanceOn: 'click',
    side: 'right',
  },
  {
    selector: target(TUTORIAL_TARGETS.splitRecord),
    title: 'Split the record',
    body: 'Click Split Record to break this document into smaller ones — useful when one fax contains several patients’ paperwork.',
    advanceOn: 'click',
  },
  {
    selector: [target(TUTORIAL_TARGETS.splitPages), MODAL_SELECTOR],
    title: 'Choose the pages',
    body: 'Enter page ranges print-style: 1-3 extracts pages 1 to 3, 5 extracts just page 5, and 1-3, 5 does both as separate documents.',
    advanceOn: 'next',
    optional: true,
  },
  {
    selector: MODAL_SELECTOR,
    title: 'What Split does',
    body: 'Clicking Split creates new records from those ranges and keeps the original intact. The tour ends here — click Cancel to leave this document untouched.',
    advanceOn: 'next',
    optional: true,
  },
];

/**
 * The v1 core four (`core: true` — they drive the backend
 * `has_completed_tutorial` flag), followed by the optional extras.
 * Order is the order shown in the checklist.
 * Add a new tutorial by appending an entry — nothing else needs to change.
 */
export const TUTORIAL_DEFINITIONS = [
  {
    id: 'upload-document',
    core: true,
    title: 'Upload a document',
    description: 'Bring a file into Medsender.',
    path: UPLOADS_PATH,
    // The real upload hard-navigates away; see uploadDocumentSteps.
    completesOn: 'upload',
    buildSteps: uploadDocumentSteps,
  },
  {
    id: 'send-to-chart',
    core: true,
    title: 'Send a document to a chart',
    description: 'File a record into a patient chart.',
    // Falls back to a "send as fax" variant when no EMR is connected, so the
    // tutorial stays useful instead of being hidden.
    dynamicTitle: ({ hasEmrIntegration }) => (
      hasEmrIntegration ? 'Send a document to a chart' : 'Send a document out'
    ),
    dynamicDescription: ({ hasEmrIntegration }) => (
      hasEmrIntegration
        ? 'File a record into a patient chart.'
        : 'No EMR connected yet — learn the fax and email path.'
    ),
    path: UPLOADS_PATH,
    requires: ({ hasRecords }) => hasRecords,
    unavailableReason: 'Upload or receive a document first — this tutorial needs a record to work with.',
    // The two variants teach two different actions, so they declare two
    // different outcomes. Both are real redux outcomes now
    // (`tutorialOutcomes.js`): the chart variant settles on the chart upload the
    // drawer performs (FINISH_AUTOUPLOAD / FAILED_AUTOUPLOAD), the fax variant on
    // the send.
    //
    // This used to be `completesOn: 'send'` plus a click list plus an opt-in to
    // the vanished-target rule, because the chart actions had no outcome at all —
    // clicking one closes the dropdown, so the tour inferred completion from its
    // own target disappearing. Cancelling the dropdown looks identical, and a
    // chart upload that then FAILED still awarded the checkmark.
    completesOn: ({ hasEmrIntegration }) => (hasEmrIntegration ? 'chart' : 'send'),
    buildSteps: sendToChartSteps,
  },
  {
    id: 'archive-document',
    core: true,
    title: 'Archive a document',
    description: 'Clear finished work out of your list.',
    path: UPLOADS_PATH,
    requires: ({ hasRecords }) => hasRecords,
    unavailableReason: 'Upload or receive a document first — this tutorial needs a record to work with.',
    // Archiving is asynchronous, so ONLY the reported result records completion.
    // The final step's `actionResult: 'archive'` is what holds the tour there
    // until the request settles — without it, the click would run the tour off
    // the end of its steps and complete the lesson on an archive that failed.
    //
    // Both handlers behind the one button report their outcome as 'archive'.
    //
    // `viewingArchivedRecords` is QUERY-STRING state on this same path, so path
    // pinning does NOT bound which handler runs: in the archived view that button
    // is Unarchive and routes to `unarchiveRecordOpen`. It deliberately emits
    // 'archive' too, so a user who reaches the last step from the archived view
    // completes on the unarchive — the lesson being taught is "this button clears
    // the record out of the list you are looking at", which is true either way.
    completesOn: 'archive',
    buildSteps: archiveDocumentSteps,
  },
  {
    id: 'filters-search-labels',
    core: true,
    title: 'Filters, search and labels',
    description: 'Find anything and organize it your way.',
    // Runs on the Inbox because labels are an inbox action; the Uploads page
    // has search and filters but no label button.
    path: INBOX_PATH,
    // All three steps target the Inbox search, filter and label controls
    // (`record-search`/`record-filters` in InboxRedesign/InboxFilterBar,
    // `label-action` from ReferralActionBar in RedesignPage). Gated on records
    // alone now that the redesigned Inbox is the only Inbox (ENG-3557); the reason
    // below is surfaced by `start()` when those controls are not on screen.
    requires: ({ hasRecords }) => hasRecords,
    unavailableReason: 'This tutorial runs on the Inbox — open the Inbox and wait for it to finish loading, then try again.',
    // No `completesOn`: opening the label control is the whole lesson, and it has
    // no asynchronous outcome to be truthful about. The click is the tour's LAST
    // step, so advancing past it completes the tutorial — which is what the
    // duplicate `completesOnClick` list was doing a second time.
    buildSteps: filtersSearchLabelsSteps,
  },
  {
    id: 'assign-record',
    title: 'Assign a record',
    description: 'Route an inbox document to a teammate.',
    path: INBOX_PATH,
    requires: ({ hasRecords }) => hasRecords,
    // Shown when step 1 finds no assignable row — an empty inbox, or one holding
    // only replies and form submissions, which Assign Document refuses.
    unavailableReason: 'This tutorial needs an inbox document that can be assigned — replies and form submissions are excluded. Clear your filters or search until an ordinary document is showing, then try again.',
    // Submitting the assignment modal is the action being taught — and it is
    // asynchronous, so the reported result decides, never the click.
    completesOn: 'assign',
    buildSteps: assignRecordSteps,
  },
  {
    id: 'send-fax',
    title: 'Send a fax',
    description: 'Fax or email a document to anyone.',
    path: UPLOADS_PATH,
    requires: ({ hasRecords }) => hasRecords,
    unavailableReason: 'Upload or receive a document first — this tutorial needs a record to work with.',
    // The tour never CLICKS Send itself. If the user does, the send's real
    // outcome completes the lesson; if they read to the end and press Done, the
    // walkthrough completed on its own terms. Observing the click was a third
    // answer to the same question and got it wrong for a failed send.
    completesOn: 'send',
    buildSteps: sendFaxSteps,
  },
  {
    id: 'split-document',
    title: 'Split a document',
    description: 'Break one document into several records.',
    path: UPLOADS_PATH,
    // Step 1 opens the record viewer (/app/view/:id) via react-router; this
    // tells LearnMedsender's navigation guard not to end the tutorial there.
    allowedPathPrefixes: ['/app/view/'],
    requires: ({ hasRecords }) => hasRecords,
    unavailableReason: 'Upload or receive a document first — this tutorial needs a record to split.',
    // Judged on the split's real outcome if the user performs one. The copy
    // invites them to Cancel instead, and that is a legitimate finish — the last
    // step is a Next step, so pressing Done completes the walkthrough without
    // creating anything.
    //
    // Cancelling used to complete it for the WRONG reason: the vanished-target
    // rule read the submit button disappearing as "the user performed the
    // action", and Cancel destroys it just the same.
    completesOn: 'split',
    buildSteps: splitDocumentSteps,
  },
];

const defaultContext = {
  hasEmrIntegration: false,
  hasRecords: false,
};

/**
 * Resolves the definitions into menu-ready descriptors for a given context.
 *
 * `available` drives the disabled state + tooltip in the checklist, AND — now that
 * it has a real signal behind it — excuses a structurally-unavailable tutorial from
 * the durable "all done" test (see `areAllTutorialsComplete`).
 */
export const buildTutorials = (context = {}) => {
  const resolved = { ...defaultContext, ...context };

  return TUTORIAL_DEFINITIONS.map((definition) => {
    const available = typeof definition.requires === 'function'
      ? !!definition.requires(resolved)
      : true;

    return {
      id: definition.id,
      core: definition.core === true,
      allowedPathPrefixes: definition.allowedPathPrefixes || [],
      title: typeof definition.dynamicTitle === 'function'
        ? definition.dynamicTitle(resolved)
        : definition.title,
      description: typeof definition.dynamicDescription === 'function'
        ? definition.dynamicDescription(resolved)
        : definition.description,
      path: definition.path,
      available,
      // The taught action whose SUCCESS completes this tutorial, wherever the tour
      // has got to — reported by `tutorialOutcomes.js` from the redux action the
      // app already dispatches. Needed even when the tour is still on screen,
      // because some taught actions tear the page down before a teardown hook
      // could record anything. A function when the answer depends on the org (the
      // send-to-chart variants teach two different actions).
      completesOn: typeof definition.completesOn === 'function'
        ? definition.completesOn(resolved)
        : (definition.completesOn || null),
      // Carried UNCONDITIONALLY. Nulling it whenever `available` was true meant the
      // six hand-written explanations could never be shown — `available` was
      // constant true — so `start()`'s failure path always fell back to the generic
      // "isn't available on this screen right now" instead of naming the actual
      // reason. The string is useful in both states: the checklist tooltip uses it
      // when the tutorial is pre-locked, and `start()` uses it when the first target
      // does not resolve at run time.
      unavailableReason: definition.unavailableReason || null,
      steps: definition.buildSteps(resolved),
    };
  });
};

export const findTutorial = (tutorials, tutorialId) => (
  (tutorials || []).find((tutorial) => tutorial.id === tutorialId) || null
);
